import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, ArrowLeft, Loader2, Mail, KeyRound, User, ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const normalizeEmail = (value) => value.trim().toLowerCase();

// ---- OTP Input Component ----
const OTPInput = ({ value, onChange, length = 6 }) => {
  const inputs = useRef([]);

  const handleChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const newOtp = value.split('');
    newOtp[idx] = val.slice(-1);
    const joined = newOtp.join('');
    onChange(joined);
    if (val && idx < length - 1) {
      inputs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted.padEnd(length, ''));
    const focusIdx = Math.min(pasted.length, length - 1);
    inputs.current[focusIdx]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          data-testid={`otp-digit-${i}`}
          className="w-12 h-14 text-center text-2xl font-bold bg-[#121212] border border-[#27272A] rounded-lg text-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all"
        />
      ))}
    </div>
  );
};

// ---- Step Indicator ----
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center gap-2 mb-6">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
          i < currentStep ? 'bg-violet-500 text-white' :
          i === currentStep ? 'bg-violet-500/20 border-2 border-violet-500 text-violet-400' :
          'bg-[#1a1a1a] border border-[#27272A] text-[#71717A]'
        }`}>
          {i < currentStep ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
        </div>
        {i < totalSteps - 1 && (
          <div className={`w-8 h-0.5 ${i < currentStep ? 'bg-violet-500' : 'bg-[#27272A]'}`} />
        )}
      </div>
    ))}
  </div>
);

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [step, setStep] = useState(0);
  // Fields
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  // UI state
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRequestInFlight = useRef(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Reset state when switching modes
  const switchMode = (newMode) => {
    setMode(newMode);
    setStep(0);
    setOtp('');
    setOtpToken('');
    setPassword('');
    setConfirmPassword('');
    setResendTimer(0);
  };

  // ---- REGISTER FLOW ----
  const handleSendRegisterOTP = async (e) => {
    e.preventDefault();
    if (!email || !username || otpRequestInFlight.current) return;
    const normalizedEmail = normalizeEmail(email);
    if (username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    otpRequestInFlight.current = true;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, {
        email: normalizedEmail, username, otp_type: 'register'
      });
      toast.success('Verification code sent to your email!');
      setStep(1);
      setResendTimer(60);
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']) || 60;
        setStep(1);
        setResendTimer(retryAfter);
        toast.info('A verification code was already sent. Check your email.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send code');
      }
    } finally {
      otpRequestInFlight.current = false;
      setLoading(false);
    }
  };

  const handleVerifyRegisterOTP = async (e) => {
    e.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    if (otp.length < 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, {
        email: normalizedEmail, otp, otp_type: 'register'
      });
      setOtpToken(res.data.otp_token);
      toast.success('Email verified!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: normalizeEmail(email), password, username, otp_token: otpToken
      });
      const { access_token, user: userData } = res.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      toast.success('Account created successfully!');
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- FORGOT PASSWORD FLOW ----
  const handleSendResetOTP = async (e) => {
    e.preventDefault();
    if (!email || otpRequestInFlight.current) return;
    const normalizedEmail = normalizeEmail(email);
    otpRequestInFlight.current = true;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, {
        email: normalizedEmail, otp_type: 'reset'
      });
      toast.success('If an account exists, a reset code has been sent.');
      setStep(1);
      setResendTimer(60);
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']) || 60;
        setStep(1);
        setResendTimer(retryAfter);
        toast.info('A verification code was already sent. Check your email.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send code');
      }
    } finally {
      otpRequestInFlight.current = false;
      setLoading(false);
    }
  };

  const handleVerifyResetOTP = async (e) => {
    e.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    if (otp.length < 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, {
        email: normalizedEmail, otp, otp_type: 'reset'
      });
      setOtpToken(res.data.otp_token);
      toast.success('Code verified!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        otp_token: otpToken,
        new_password: password
      });
      toast.success('Password reset! You can now sign in.');
      switchMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- LOGIN ----
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // ---- Resend OTP ----
  const handleResendOTP = async () => {
    if (resendTimer > 0 || otpRequestInFlight.current) return;
    otpRequestInFlight.current = true;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, {
        email: normalizeEmail(email),
        username: mode === 'register' ? username : undefined,
        otp_type: mode === 'register' ? 'register' : 'reset'
      });
      toast.success('New code sent!');
      setOtp('');
      setResendTimer(60);
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']) || 60;
        setResendTimer(retryAfter);
        toast.info(`Please wait ${retryAfter} seconds before requesting another code.`);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to resend');
      }
    } finally {
      otpRequestInFlight.current = false;
      setLoading(false);
    }
  };

  // ---- RENDER FORMS ----
  const slideVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  const renderLoginForm = () => (
    <motion.form key="login" variants={slideVariants} initial="enter" animate="center" exit="exit" onSubmit={handleLogin} className="space-y-5">
      <div>
        <Label htmlFor="email" className="text-[#EDEDED] font-medium">Email</Label>
        <Input id="email" data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="you@example.com" required />
      </div>
      <div>
        <Label htmlFor="password" className="text-[#EDEDED] font-medium">Password</Label>
        <Input id="password" data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="Enter your password" required minLength={6} />
      </div>
      <Button data-testid="submit-btn" type="submit" disabled={loading}
        className="w-full h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold glow-violet">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign in'}
      </Button>
      <button type="button" data-testid="forgot-password-btn" onClick={() => switchMode('forgot')}
        className="w-full text-center text-sm text-violet-400 hover:text-violet-300 transition-colors">
        Forgot your password?
      </button>
    </motion.form>
  );

  const renderRegisterStep0 = () => (
    <motion.form key="reg-0" variants={slideVariants} initial="enter" animate="center" exit="exit" onSubmit={handleSendRegisterOTP} className="space-y-5">
      <div>
        <Label htmlFor="username" className="text-[#EDEDED] font-medium">Username</Label>
        <div className="mt-2">
          <div className="flex items-center bg-[#121212] border border-[#27272A] rounded-md overflow-hidden focus-within:border-violet-500 focus-within:ring-1 focus-within:ring-violet-500">
            <span className="text-[#71717A] text-sm pl-4 pr-1 whitespace-nowrap flex-shrink-0">createonelink.com/</span>
            <input id="username" data-testid="username-input" type="text" value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              className="bg-transparent text-white h-12 w-full outline-none border-none pr-4"
              placeholder="yourname" required />
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="email" className="text-[#EDEDED] font-medium">Email</Label>
        <Input id="email" data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="you@example.com" required />
      </div>
      <Button data-testid="send-otp-btn" type="submit" disabled={loading}
        className="w-full h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold glow-violet flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Mail className="w-4 h-4" /> Send Verification Code</>}
      </Button>
    </motion.form>
  );

  const renderOTPStep = (onSubmit) => (
    <motion.form key="otp" variants={slideVariants} initial="enter" animate="center" exit="exit" onSubmit={onSubmit} className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/10 border border-violet-500/30 mb-4">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <p className="text-[#A1A1AA] text-sm">We sent a 6-digit code to</p>
        <p className="text-white font-medium">{email}</p>
      </div>
      <OTPInput value={otp} onChange={setOtp} />
      <Button data-testid="verify-otp-btn" type="submit" disabled={loading || otp.replace(/ /g, '').length < 6}
        className="w-full h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold glow-violet flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><KeyRound className="w-4 h-4" /> Verify Code</>}
      </Button>
      <div className="text-center">
        {resendTimer > 0 ? (
          <p className="text-[#71717A] text-sm">Resend code in <span className="text-violet-400">{resendTimer}s</span></p>
        ) : (
          <button type="button" data-testid="resend-otp-btn" onClick={handleResendOTP}
            className="text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors">
            Resend code
          </button>
        )}
      </div>
    </motion.form>
  );

  const renderPasswordStep = (onSubmit, buttonText) => (
    <motion.form key="password" variants={slideVariants} initial="enter" animate="center" exit="exit" onSubmit={onSubmit} className="space-y-5">
      <div className="text-center mb-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-400" />
        </div>
        <p className="text-green-400 font-medium text-sm">Email verified</p>
      </div>
      <div>
        <Label htmlFor="password" className="text-[#EDEDED] font-medium">
          {mode === 'register' ? 'Create Password' : 'New Password'}
        </Label>
        <Input id="password" data-testid="password-input" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="Min 6 characters" required minLength={6} />
      </div>
      <div>
        <Label htmlFor="confirm-password" className="text-[#EDEDED] font-medium">Confirm Password</Label>
        <Input id="confirm-password" data-testid="confirm-password-input" type="password" value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="Confirm your password" required minLength={6} />
      </div>
      <Button data-testid="complete-btn" type="submit" disabled={loading}
        className="w-full h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold glow-violet flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> {buttonText}</>}
      </Button>
    </motion.form>
  );

  // ---- FORGOT PASSWORD STEP 0: Enter email ----
  const renderForgotStep0 = () => (
    <motion.form key="forgot-0" variants={slideVariants} initial="enter" animate="center" exit="exit" onSubmit={handleSendResetOTP} className="space-y-5">
      <div className="text-center mb-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/10 border border-violet-500/30 mb-4">
          <KeyRound className="w-8 h-8 text-violet-400" />
        </div>
        <p className="text-[#A1A1AA] text-sm">Enter the email associated with your account</p>
      </div>
      <div>
        <Label htmlFor="email" className="text-[#EDEDED] font-medium">Email</Label>
        <Input id="email" data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-2 bg-[#121212] border-[#27272A] text-white h-12 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          placeholder="you@example.com" required />
      </div>
      <Button data-testid="send-reset-otp-btn" type="submit" disabled={loading}
        className="w-full h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold glow-violet flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Mail className="w-4 h-4" /> Send Reset Code</>}
      </Button>
    </motion.form>
  );

  // ---- MAIN HEADING & CONTENT ----
  const getHeading = () => {
    if (mode === 'login') return { title: 'Welcome back', subtitle: 'Sign in to manage your links and profile' };
    if (mode === 'register') {
      if (step === 0) return { title: 'Create your account', subtitle: 'Start sharing your links in minutes' };
      if (step === 1) return { title: 'Verify your email', subtitle: 'Enter the code we sent you' };
      return { title: 'Set your password', subtitle: 'Almost there! Create a secure password' };
    }
    // forgot
    if (step === 0) return { title: 'Reset password', subtitle: 'We\'ll send you a verification code' };
    if (step === 1) return { title: 'Check your email', subtitle: 'Enter the code we sent you' };
    return { title: 'New password', subtitle: 'Create a new password for your account' };
  };

  const renderContent = () => {
    if (mode === 'login') return renderLoginForm();
    if (mode === 'register') {
      if (step === 0) return renderRegisterStep0();
      if (step === 1) return renderOTPStep(handleVerifyRegisterOTP);
      return renderPasswordStep(handleCompleteRegister, 'Create Account');
    }
    // forgot
    if (step === 0) return renderForgotStep0();
    if (step === 1) return renderOTPStep(handleVerifyResetOTP);
    return renderPasswordStep(handleResetPassword, 'Reset Password');
  };

  const { title, subtitle } = getHeading();

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="max-w-md w-full mx-auto">
          <button data-testid="back-btn"
            onClick={() => {
              if (mode !== 'login' && step > 0) {
                setStep(step - 1);
                setOtp('');
              } else if (mode === 'forgot') {
                switchMode('login');
              } else {
                navigate('/');
              }
            }}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {mode !== 'login' && step > 0 ? 'Go back' : mode === 'forgot' ? 'Back to sign in' : 'Back to home'}
          </button>

          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">One Link</span>
          </div>

          {(mode === 'register' || mode === 'forgot') && (
            <StepIndicator currentStep={step} totalSteps={3} />
          )}

          <h1 className="font-outfit font-bold text-3xl text-white mb-2">{title}</h1>
          <p className="text-[#A1A1AA] mb-8">{subtitle}</p>

          <AnimatePresence mode="wait">
            {renderContent()}
          </AnimatePresence>

          {mode !== 'forgot' && (
            <p className="mt-8 text-center text-[#A1A1AA]">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              <button data-testid="toggle-auth-btn" onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="ml-2 text-violet-400 hover:text-violet-300 font-medium">
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          )}
        </motion.div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative z-10">
          <div className="phone-frame p-4 w-64">
            <div className="h-full flex flex-col items-center pt-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500" />
              <div className="w-24 h-3 bg-white/20 rounded mt-4" />
              <div className="w-16 h-2 bg-white/10 rounded mt-2" />
              <div className="w-full mt-6 space-y-3 px-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-full h-10 rounded-xl bg-white/5 border border-white/10" />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
